import { appAlert } from '@/lib/appAlert';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useProfile } from '@/contexts/ProfileContext';
import { supabase } from '@/lib/supabase';

export default function ManageAdminsScreen() {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [userId, setUserId] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastAction, setLastAction] = useState<{ id: string; granted: boolean } | null>(null);

  const isAdmin = !!profile?.is_admin;

  if (!isAdmin) {
    return (
      <View style={[styles.center, { paddingBottom: insets.bottom }]}>
        <Text style={styles.errorText}>{t('adminScreen.noAccessScreen')}</Text>
      </View>
    );
  }

  async function handleSet(makeAdmin: boolean) {
    const id = userId.trim();
    if (!id) return;

    const label = makeAdmin ? t('adminScreen.confirmGrantTitle') : t('adminScreen.confirmRevokeTitle');
    appAlert(
      label,
      t('adminScreen.confirmUserIdBody', { id }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.confirm'),
          style: makeAdmin ? 'default' : 'destructive',
          onPress: async () => {
            setBusy(true);
            try {
              const { error } = await supabase.rpc('set_admin', {
                target_user_id: id,
                make_admin: makeAdmin,
              });
              if (error) throw error;
              setLastAction({ id, granted: makeAdmin });
              setUserId('');
            } catch (e: any) {
              appAlert(t('common.error'), e.message ?? t('adminScreen.setAdminFailed'));
            } finally {
              setBusy(false);
            }
          },
        },
      ]
    );
  }

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView
        contentContainerStyle={[styles.container, { paddingBottom: insets.bottom + 32 }]}
        keyboardShouldPersistTaps="handled"
      >
        <Pressable onPress={() => router.back()} style={styles.back}>
          <Text style={[styles.backText, { color: colors.primary }]}>{t('common.back')}</Text>
        </Pressable>

        <Text style={styles.title}>{t('adminScreen.manageAdminsTitle')}</Text>
        <Text style={styles.subtitle}>
          {t('adminScreen.manageAdminsSubtitle')}
        </Text>

        <View style={styles.card}>
          <Text style={styles.label}>{t('adminScreen.userIdLabel')}</Text>
          <TextInput
            style={styles.input}
            value={userId}
            onChangeText={setUserId}
            placeholder={t('adminScreen.userIdPlaceholder')}
            placeholderTextColor={colors.textMuted}
            autoCapitalize="none"
            autoCorrect={false}
            editable={!busy}
          />

          <View style={styles.btnRow}>
            <Button
              title={t('adminScreen.grantAdmin')}
              onPress={() => handleSet(true)}
              disabled={!userId.trim() || busy}
              loading={busy}
              style={styles.btnGrant}
            />
            <Button
              title={t('adminScreen.revokeAdmin')}
              onPress={() => handleSet(false)}
              disabled={!userId.trim() || busy}
              loading={busy}
              variant="secondary"
              style={styles.btnRevoke}
            />
          </View>
        </View>

        {lastAction && (
          <View style={[styles.successBanner, { borderColor: lastAction.granted ? colors.success : colors.danger }]}>
            <Text style={[styles.successText, { color: lastAction.granted ? colors.success : colors.danger }]}>
              {lastAction.granted ? t('adminScreen.adminGranted') : t('adminScreen.adminRevoked')}
            </Text>
            <Text style={styles.successId} numberOfLines={1}>{lastAction.id}</Text>
          </View>
        )}

        <View style={styles.tipCard}>
          <Text style={styles.tipTitle}>{t('adminScreen.tipTitle')}</Text>
          <Text style={styles.tipBody}>
            {t('adminScreen.tipBody')}
          </Text>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.lg,
    },
    errorText: {
      fontSize: fontSize.md,
      color: colors.danger,
      textAlign: 'center',
    },
    container: {
      padding: spacing.lg,
      gap: spacing.lg,
    },
    back: {
      alignSelf: 'flex-start',
    },
    backText: {
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    title: {
      fontSize: fontSize.xl,
      fontWeight: '700',
      color: colors.text,
    },
    subtitle: {
      fontSize: fontSize.sm,
      color: colors.textMuted,
      lineHeight: 20,
    },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      padding: spacing.lg,
      gap: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    label: {
      fontSize: fontSize.sm,
      fontWeight: '600',
      color: colors.text,
    },
    input: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      padding: spacing.md,
      fontSize: fontSize.sm,
      color: colors.text,
      backgroundColor: colors.background,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
    btnRow: {
      flexDirection: 'row',
      gap: spacing.sm,
    },
    btnGrant: {
      flex: 1,
    },
    btnRevoke: {
      flex: 1,
    },
    successBanner: {
      borderWidth: 1,
      borderRadius: radius.md,
      padding: spacing.md,
      gap: 4,
    },
    successText: {
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    successId: {
      fontSize: fontSize.xs,
      color: colors.textMuted,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
    tipCard: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      padding: spacing.md,
      gap: spacing.xs,
      borderWidth: 1,
      borderColor: colors.border,
    },
    tipTitle: {
      fontSize: fontSize.sm,
      fontWeight: '700',
      color: colors.text,
    },
    tipBody: {
      fontSize: fontSize.xs,
      color: colors.textMuted,
      lineHeight: 18,
    },
  });
}
